/**
 * Globo con número en el ícono de la app instalada.
 * - Escritorio e iPhone (app en la pantalla de inicio): Badging API, con el número exacto.
 * - Android: Chrome no tiene Badging API; el lanzador cuenta las notificaciones de KEORA que siguen
 *   en la barra. Por eso, al salir de la app, cada aviso sin leer que no esté en la barra se vuelve
 *   a dejar ahí (sin sonido) y el globo coincide con la campana. Al leerlos se quitan de la barra.
 */
type BadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

/** Lo mínimo de un aviso sin leer para dejarlo en la barra. */
export interface UnreadItem {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
}

/** Cuántos avisos sin leer se dejan en la barra como máximo. */
const TRAY_MAX = 20;

let unread: UnreadItem[] = [];
let watching = false;

const nav = () => (typeof navigator === 'undefined' ? undefined : (navigator as BadgeNavigator));

/** App instalada en Android (ahí el globo sale de las notificaciones en la barra). */
function androidInstalled(): boolean {
  const n = nav();
  if (!n || !/Android/i.test(n.userAgent)) return false;
  return typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches === true;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  const n = nav();
  if (!n || !('serviceWorker' in n) || typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
  return (await n.serviceWorker.getRegistration()) ?? null;
}

async function closeTray(filter: (n: Notification) => boolean): Promise<void> {
  const reg = await registration();
  if (!reg) return;
  (await reg.getNotifications()).filter(filter).forEach((n) => n.close());
}

/** Los avisos que la persona quitó de la barra (los guarda sw.js): no se vuelven a poner. */
async function dismissed(): Promise<Set<string>> {
  if (typeof caches === 'undefined') return new Set();
  const hit = await (await caches.open('keora-descartadas')).match('/descartadas');
  return new Set(hit ? ((await hit.json()) as string[]) : []);
}

/** Al salir de la app deja en la barra los avisos sin leer que falten (Android), menos los que la persona quitó. */
async function fillTray(): Promise<void> {
  const reg = await registration();
  if (!reg || unread.length === 0) return;
  const shown = new Set((await reg.getNotifications()).map((n) => n.tag));
  const removed = await dismissed().catch(() => new Set<string>());
  for (const n of unread.slice(0, TRAY_MAX).reverse()) {
    if (shown.has(n.id) || removed.has(n.id)) continue;
    // `actions` (botón «Marcar todas como leídas») aún no está en los tipos de TypeScript.
    await reg.showNotification(n.title, {
      body: n.body ?? undefined,
      icon: '/icon-192.png',
      badge: '/badge-96.png',
      tag: n.id,
      silent: true,
      actions: [{ action: 'leer-todas', title: 'Marcar todas como leídas' }],
      data: { url: n.link?.startsWith('/app') ? n.link : '/app/notificaciones' },
    } as NotificationOptions);
  }
}

function watchVisibility(): void {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void fillTray().catch(() => undefined);
  });
}

/**
 * Lo sin leer cambió: número del ícono y, en Android, lo que queda en la barra. Los avisos de grupo
 * que ya atendió alguien (`resolved`) se quitan de la barra en cualquier celular o computadora.
 */
export function setAppBadge(count: number, items: readonly UnreadItem[] = [], resolved: readonly string[] = []): void {
  unread = [...items];
  if (resolved.length > 0) {
    const done = new Set(resolved);
    void closeTray((t) => done.has(t.tag)).catch(() => undefined);
  }
  const n = nav();
  if (n?.setAppBadge) {
    const done = count > 0 ? n.setAppBadge(count) : n.clearAppBadge ? n.clearAppBadge() : n.setAppBadge(0);
    void done?.catch(() => undefined);
  }
  if (!androidInstalled()) return;
  watchVisibility();
  // Lo ya leído (aquí o en otro dispositivo) sale de la barra; al leer todo no queda nada.
  const ids = new Set(unread.map((i) => i.id));
  void closeTray((t) => count === 0 || !ids.has(t.tag)).catch(() => undefined);
}

/** Al abrir un aviso en la app se quita de la barra del celular. */
export function closeTrayNotification(id: string): void {
  void closeTray((n) => n.tag === id).catch(() => undefined);
}
